// API client for status page backend
const API_BASE = '/api/v1';

/**
 * Fetch overall system status
 * @returns {Promise<{status: string, message: string, checked_at: string}>}
 */
export async function fetchOverallStatus() {
  const response = await fetch(`${API_BASE}/status/overall`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch all services with their current status
 * @returns {Promise<{services: Array<{name: string, slug: string, status: string, latency_ms: number, checked_at: string}>}>}
 */
export async function fetchServices() {
  const response = await fetch(`${API_BASE}/status/services`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch specific service details with uptime bars
 * @param {string} slug - Service slug
 * @returns {Promise<{name: string, slug: string, status: string, latency_ms: number, checked_at: string, uptime_bars: Array}>}
 */
export async function fetchService(slug) {
  const response = await fetch(`${API_BASE}/status/services/${slug}`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch uptime bars for a specific service
 * @param {string} slug - Service slug
 * @returns {Promise<{slug: string, bars: Array<{date: string, status: string, uptime_percentage: number}>}>}
 */
export async function fetchUptime(slug) {
  const response = await fetch(`${API_BASE}/status/services/${slug}/uptime`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch recent incidents
 * @returns {Promise<{incidents: Array}>}
 */
export async function fetchIncidents() {
  const response = await fetch(`${API_BASE}/incidents`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch active (ongoing) incidents
 * @returns {Promise<{incidents: Array}>}
 */
export async function fetchActiveIncidents() {
  const response = await fetch(`${API_BASE}/incidents/active`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Create a new subscription
 * @param {Object} params - Subscription parameters
 * @param {string} params.channel - 'email' or 'webhook'
 * @param {string} [params.email] - Email address (required for email channel)
 * @param {string} [params.webhook_url] - Webhook URL (required for webhook channel)
 * @returns {Promise<{message: string, status: string}>}
 */
export async function createSubscription(params) {
  const response = await fetch(`${API_BASE}/subscriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(params)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}
